import { Modal, Checkbox, Spin } from "antd";
import { AppButton } from "components/button/AppButton";
import { useGetPermissions } from "../hooks/useGetPermissions";
import { openNotification } from "utils/notification";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useAsignRoles } from "../hooks/useAsignRoles";
import { useQueryClient } from "react-query";
import { QUERY_KEY_FOR_ROLES } from "../hooks/useGetRoles";
import { useEffect, useState } from "react";
import {
  AssignPermissionsRequest,
  AuthorizationGuard,
  AuthorizationRole,
} from "../types";
import { getAuthorizationErrorMessage } from "../authorizationError";

interface AssignPermissionsProps {
  open: boolean;
  guardName: AuthorizationGuard;
  role: AuthorizationRole | null;
  handleClose: () => void;
}

export const AsynPermissions = ({
  open,
  guardName,
  role,
  handleClose,
}: AssignPermissionsProps) => {
  const { data, isLoading: permissionsLoading } = useGetPermissions({
    guardName,
  });
  const { token } = useGetUserInfo();
  const [checkedPermissions, setCheckedPermissions] = useState<number[]>([]);
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useAsignRoles();

  useEffect(() => {
    if (open) {
      setCheckedPermissions(
        role?.permissions.map((permission) => permission.id) ?? []
      );
    }
  }, [open, role]);

  const handleFormSubmit = () => {
    if (!role) {
      return;
    }

    const props: AssignPermissionsRequest = {
      token,
      roleId: role.id,
      guard_name: guardName,
      permission_ids: checkedPermissions,
    };

    mutate(props, {
      onError: (error) => {
        openNotification({
          title: "Error",
          state: "error",
          description: getAuthorizationErrorMessage(
            error,
            "Could not assign permissions"
          ),
          duration: 6.0,
        });
      },
      onSuccess: () => {
        openNotification({
          title: "Success",
          state: "success",
          description: "Permissions assigned successfully",
          duration: 4.5,
        });
        queryClient.invalidateQueries([QUERY_KEY_FOR_ROLES, guardName]);
        handleClose();
      },
    });
  };

  const onChange = (checkedValues: Array<number | string>) => {
    setCheckedPermissions(checkedValues.map(Number));
  };

  return (
    <Modal
      style={{ top: 20 }}
      footer={false}
      open={open}
      onCancel={handleClose}
      title={`Assign Permissions${role ? ` — ${role.name}` : ""}`}
      width="50rem"
      destroyOnHidden
    >
      <div className="py-8">
        <Spin spinning={permissionsLoading}>
          <Checkbox.Group value={checkedPermissions} onChange={onChange}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data?.data.map((permission) => (
                <Checkbox key={permission.id} value={permission.id}>
                  {permission.name}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        </Spin>
      </div>
      <AppButton
        label="Submit"
        isLoading={isLoading}
        disabled={!role}
        handleClick={handleFormSubmit}
      />
    </Modal>
  );
};
